import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';
import {
  documentCategories
} from '../data/corporateContent';
import { ROUTE_PATHS } from '../config/navigation';
import { hasPermission } from '../utils/roles';
import useDebouncedValue from '../hooks/useDebouncedValue';

const DOCUMENTS_PER_PAGE = 50;
const EMPTY_CATEGORY_COUNTS = Object.fromEntries(documentCategories.map((category) => [category.id, 0]));

const DocumentsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 350);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ current: 1, pages: 1, total: 0, limit: DOCUMENTS_PER_PAGE });
  const [categoryCounts, setCategoryCounts] = useState(EMPTY_CATEGORY_COUNTS);
  const [folderCount, setFolderCount] = useState(0);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadMode, setUploadMode] = useState('single');
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingDocument, setEditingDocument] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    files: [],
    category: 'other'
  });
  const hasLoadedRef = useRef(false);
  const requestSequenceRef = useRef(0);
  const { toasts, showToast, removeToast } = useToast();
  const documentAccess = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('userData') || '{}');
      const isAdmin = ['super_admin', 'company_admin'].includes(user.role);
      return {
        create: isAdmin || hasPermission(user, 'documents', 'create'),
        edit: isAdmin || hasPermission(user, 'documents', 'edit'),
        delete: isAdmin || hasPermission(user, 'documents', 'delete')
      };
    } catch (error) { return { create: false, edit: false, delete: false }; }
  }, []);
  const createSampleDocuments = useCallback(async () => {
    const sampleDocuments = [
      {
        name: 'İnsan Kaynakları Politikaları',
        type: 'folder',
        size: null,
        modified: new Date().toISOString(),
        icon: 'bx bxs-folder',
        color: 'text-warning',
        category: 'policies'
      },
      {
        name: 'Kalite Yönetim Sistemleri',
        type: 'folder',
        size: null,
        modified: new Date().toISOString(),
        icon: 'bx bxs-folder',
        color: 'text-warning',
        category: 'policies'
      },
      {
        name: 'Proje Şablonları',
        type: 'folder',
        size: null,
        modified: new Date().toISOString(),
        icon: 'bx bxs-folder',
        color: 'text-warning',
        category: 'forms'
      },
      {
        name: 'İş Sağlığı ve Güvenliği',
        type: 'folder',
        size: null,
        modified: new Date().toISOString(),
        icon: 'bx bxs-folder',
        color: 'text-warning',
        category: 'policies'
      },
      {
        name: 'Yıllık İzin Prosedürü.pdf',
        type: 'pdf',
        size: '1.2 MB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-file-pdf',
        color: 'text-danger',
        category: 'policies'
      },
      {
        name: 'ISO_9001_Sertifikası.pdf',
        type: 'pdf',
        size: '850 KB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-file-pdf',
        color: 'text-danger',
        category: 'policies'
      },
      {
        name: 'Çalışan_El_Kitabı.docx',
        type: 'word',
        size: '2.5 MB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-file-doc',
        color: 'text-primary',
        category: 'corporate'
      },
      {
        name: 'Organizasyon_Şeması.xlsx',
        type: 'excel',
        size: '890 KB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-spreadsheet',
        color: 'text-success',
        category: 'corporate'
      },
      {
        name: 'Şirket_Sunumu.pptx',
        type: 'powerpoint',
        size: '15.3 MB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-file-export',
        color: 'text-orange',
        category: 'corporate'
      },
      {
        name: 'Güvenlik_Politikası.pdf',
        type: 'pdf',
        size: '675 KB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-file-pdf',
        color: 'text-danger',
        category: 'policies'
      },
      {
        name: 'Masraf_Bildirim_Formu.xlsx',
        type: 'excel',
        size: '420 KB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-spreadsheet',
        color: 'text-success',
        category: 'forms'
      },
      {
        name: 'Dilekçe_Örneği.docx',
        type: 'word',
        size: '310 KB',
        modified: new Date().toISOString(),
        icon: 'bx bxs-file-doc',
        color: 'text-primary',
        category: 'forms'
      }
    ];

    // Eski örnek veri tanımları ağ hatasında kullanıcıya gerçek dokümanmış gibi
    // gösterilmez; liste güvenli biçimde boş kalır.
    void sampleDocuments;
    setDocuments([]);
    setPagination({ current: 1, pages: 1, total: 0, limit: DOCUMENTS_PER_PAGE });
    setCategoryCounts(EMPTY_CATEGORY_COUNTS);
  }, []);

  const fetchDocuments = useCallback(async () => {
    const requestId = ++requestSequenceRef.current;
    try {
      if (hasLoadedRef.current) setRefreshing(true);
      else setLoading(true);
      const params = {
        page: currentPage,
        limit: DOCUMENTS_PER_PAGE
      };

      if (debouncedSearchTerm.trim()) params.search = debouncedSearchTerm.trim();
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (currentFolderId) params.parentFolder = currentFolderId;

      const response = await api.getDocuments(params);
      if (requestId !== requestSequenceRef.current) return;
      setDocuments(response.data || []);
      setPagination(response.pagination || { current: currentPage, pages: 1, total: 0, limit: DOCUMENTS_PER_PAGE });
      setCategoryCounts(response.categoryCounts || EMPTY_CATEGORY_COUNTS);
      setFolderCount(response.folderCount || 0);
      setCurrentFolder(response.currentFolder || null);
      setBreadcrumbs(response.breadcrumbs || []);
    } catch (error) {
      if (requestId !== requestSequenceRef.current) return;
      console.error('Dokümanlar yüklenirken hata:', error);
      await createSampleDocuments();
    } finally {
      if (requestId === requestSequenceRef.current) {
        hasLoadedRef.current = true;
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [createSampleDocuments, currentFolderId, currentPage, debouncedSearchTerm, selectedCategory]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    setCurrentPage(1);
  }, [currentFolderId, debouncedSearchTerm, selectedCategory]);

  useEffect(() => {
    const requestedCategory = searchParams.get('kategori');
    const matchedCategory = documentCategories.find(
      (category) => category.queryValue === requestedCategory || category.legacyQueryValues?.includes(requestedCategory)
    );

    setSelectedCategory(matchedCategory?.id || 'all');
  }, [searchParams]);

  const inferCategory = useCallback((document) => {
    if (documentCategories.some((category) => category.id === document.category)) {
      return document.category;
    }

    const searchableText = `${document.name || ''} ${document.description || ''}`.toLocaleLowerCase('tr-TR');
    const matchedCategory = documentCategories.find(
      (category) =>
        category.id !== 'all' &&
        category.keywords.some((keyword) => searchableText.includes(keyword.toLocaleLowerCase('tr-TR')))
    );

    return matchedCategory?.id || 'other';
  }, []);

  const documentsWithCategory = useMemo(
    () => documents.map((document) => ({ ...document, resolvedCategory: inferCategory(document) })),
    [documents, inferCategory]
  );

  const filteredDocuments = documentsWithCategory;

  const visiblePageNumbers = useMemo(() => {
    if (pagination.pages <= 7) {
      return Array.from({ length: pagination.pages }, (_, index) => index + 1);
    }

    const candidates = [1, currentPage - 1, currentPage, currentPage + 1, pagination.pages]
      .filter((page) => page >= 1 && page <= pagination.pages);
    const uniquePages = [...new Set(candidates)].sort((left, right) => left - right);
    const pageItems = [];

    uniquePages.forEach((page, index) => {
      if (index > 0 && page - uniquePages[index - 1] > 1) pageItems.push(`ellipsis-${page}`);
      pageItems.push(page);
    });

    return pageItems;
  }, [currentPage, pagination.pages]);

  const setCategoryFromCard = (categoryId) => {
    setCurrentPage(1);
    setCurrentFolderId(null);
    setCurrentFolder(null);
    setBreadcrumbs([]);
    setSelectedCategory(categoryId);
    const category = documentCategories.find((item) => item.id === categoryId);

    if (!category || category.id === 'all') {
      setSearchParams({});
      return;
    }

    setSearchParams({ kategori: category.queryValue });
  };

  const formatFileSize = (size) => {
    if (!size) return '-';
    return size;
  };

  const getFileTypeLabel = (type) => {
    const types = {
      folder: 'Klasör',
      pdf: 'PDF Belgesi',
      word: 'Word Belgesi',
      excel: 'Excel Belgesi',
      powerpoint: 'PowerPoint Sunumu',
      image: 'Resim',
      video: 'Video',
      other: 'Diğer'
    };

    return types[type] || 'Bilinmeyen';
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const iconMap = {
      pdf: 'bx-file-pdf',
      doc: 'bx-file-doc',
      docx: 'bx-file-doc',
      xls: 'bx-file',
      xlsx: 'bx-file',
      ppt: 'bx-file',
      pptx: 'bx-file',
      jpg: 'bx-image',
      jpeg: 'bx-image',
      png: 'bx-image',
      mp4: 'bx-video',
      gif: 'bx-image',
      txt: 'bx-file-txt',
      zip: 'bx-archive',
      rar: 'bx-archive'
    };

    return iconMap[ext] || 'bx-file';
  };

  const getActiveCategory = () => currentFolder?.category || (
    selectedCategory !== 'all' ? selectedCategory : 'other'
  );

  const openUploadModal = (mode) => {
    setUploadMode(mode);
    setUploadForm({ title: '', description: '', files: [], category: getActiveCategory() });
    setShowUploadModal(true);
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setUploadForm((prev) => ({
      ...prev,
      files,
      title: files.length === 1 ? files[0].name : ''
    }));
  };

  const handleUploadSubmit = async () => {
    if (!uploadForm.files.length) {
      showToast('Lütfen en az bir dosya seçin', 'error');
      return;
    }

    if (uploadMode === 'single' && uploadForm.files.length === 1 && !uploadForm.title.trim()) {
      showToast('Lütfen dosya başlığı girin', 'error');
      return;
    }

    if (uploadForm.files.some((file) => file.size > 1024 * 1024 * 1024)) {
      showToast("Dosya boyutu 1 GB'dan büyük olamaz", 'error');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }

          return prev + 10;
        });
      }, 100);

      if (uploadMode === 'single' && uploadForm.files.length === 1) {
        await api.uploadDocument(
          uploadForm.files[0],
          uploadForm.title,
          uploadForm.description,
          {
            category: uploadForm.category,
            parentFolder: currentFolderId
          }
        );
      } else {
        await api.uploadDocuments(uploadForm.files, {
          category: uploadForm.category,
          parentFolder: currentFolderId,
          description: uploadForm.description
        });
      }

      clearInterval(progressInterval);
      setUploadProgress(100);
      if (currentPage === 1) {
        await fetchDocuments();
      } else {
        setCurrentPage(1);
      }
      const uploadedCount = uploadForm.files.length;
      setUploadForm((current) => ({ ...current, title: '', description: '', files: [], category: 'other' }));
      setShowUploadModal(false);

      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
        showToast(uploadedCount > 1 ? `${uploadedCount} dosya başarıyla yüklendi!` : 'Dosya başarıyla yüklendi!', 'success');
      }, 500);
    } catch (error) {
      console.error('Dosya yüklenirken hata:', error);
      showToast(`Dosya yüklenirken hata oluştu: ${error.message}`, 'error');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCreateFolder = async (event) => {
    event.preventDefault();
    if (!newFolderName.trim()) return;
    try {
      await api.createDocumentFolder({
        name: newFolderName.trim(),
        parentFolder: currentFolderId,
        category: getActiveCategory()
      });
      setNewFolderName('');
      setShowFolderModal(false);
      await fetchDocuments();
      showToast('Klasör oluşturuldu.', 'success');
    } catch (error) {
      showToast(error.message || 'Klasör oluşturulamadı.', 'error');
    }
  };

  const openFolder = (folder) => {
    setCurrentFolderId(folder._id || folder.id);
    setCurrentPage(1);
    setSearchTerm('');
  };

  const navigateToFolder = (folderId) => {
    setCurrentFolderId(folderId || null);
    setCurrentPage(1);
    setSearchTerm('');
  };

  const handleDownload = async (documentId) => {
    const selectedDocument = documents.find((item) => (item._id || item.id) === documentId);

    if (selectedDocument?.type === 'folder') {
      openFolder(selectedDocument);
      return;
    }

    try {
      const url = await api.getDocumentDownloadUrl(documentId);
      const link = document.createElement('a');

      link.href = url;
      link.download = selectedDocument?.originalName || selectedDocument?.name || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast('Dosya indirme işlemi başlatıldı.', 'success');
    } catch (error) {
      console.error('Dosya indirilirken hata:', error);
      showToast(`Dosya indirilirken hata oluştu: ${error.message}`, 'error');
    }
  };

  const handleDelete = async (documentId, documentName) => {
    showToast(`"${documentName}" dosyasını silmek istediğinizden emin misiniz?`, 'warning', {
      duration: 0,
      actions: [
        {
          label: 'Evet, Sil',
          onClick: async () => {
            try {
              await api.deleteDocument(documentId);
              if (documents.length === 1 && currentPage > 1) {
                setCurrentPage((page) => page - 1);
              } else {
                await fetchDocuments();
              }
              showToast('Dosya başarıyla silindi!', 'success');
            } catch (error) {
              console.error('Dosya silinirken hata:', error);
              showToast(`Dosya silinirken hata oluştu: ${error.message}`, 'error');
            }
          }
        },
        {
          label: 'İptal',
          onClick: () => {}
        }
      ]
    });
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    try {
      const response = await api.updateDocument(editingDocument._id, {
        name: editingDocument.name,
        title: editingDocument.name,
        description: editingDocument.description || '',
        category: editingDocument.category || editingDocument.resolvedCategory || 'other'
      });
      setDocuments((current) => current.map((item) => item._id === response.data._id ? response.data : item));
      setEditingDocument(null);
      showToast('Doküman bilgileri güncellendi.', 'success');
    } catch (error) { showToast(error.message || 'Doküman güncellenemedi.', 'error'); }
  };

  if (loading) {
    return (
      <div className="modern-loading">
        <div className="text-center">
          <div className="modern-spinner spinner-border mb-3" role="status">
            <span className="visually-hidden">Yükleniyor...</span>
          </div>
          <p className="text-muted">Dokümanlar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="documents-page">
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <div className="row mb-4">
        <div className="col-12">
          <div className="modern-card p-4 corporate-documents-hero">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h3 className="fw-bold text-dark mb-1">Doküman Merkezi</h3>
              </div>
              <div className="d-flex align-items-center gap-3">
                <div className="badge bg-primary rounded-pill px-3 py-2">
                  <i className="bx bx-folder me-1"></i>
                  {pagination.total + folderCount} Öğe
                </div>
                <button className="btn btn-modern-secondary" onClick={() => navigate(ROUTE_PATHS.announcements)}>
                  Duyurular
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <div className="modern-card p-4">
            <div className="corporate-category-filters mb-4">
              {documentCategories.map((category) => (
                <button
                  key={category.id}
                  className={`corporate-filter-chip ${selectedCategory === category.id ? 'active' : ''}`}
                  onClick={() => setCategoryFromCard(category.id)}
                >
                  <i className={category.icon}></i>
                  <span>{category.shortLabel}</span>
                  <small>{categoryCounts[category.id] || 0}</small>
                </button>
              ))}
            </div>

            <div className="documents-folder-toolbar">
              <nav className="documents-breadcrumb" aria-label="Doküman klasör yolu">
                <button type="button" onClick={() => navigateToFolder(null)}>
                  <i className="bx bx-home-alt" /> Doküman Merkezi
                </button>
                {breadcrumbs.map((folder) => (
                  <React.Fragment key={folder.id}>
                    <i className="bx bx-chevron-right" aria-hidden="true" />
                    <button type="button" onClick={() => navigateToFolder(folder.id)}>
                      {folder.name}
                    </button>
                  </React.Fragment>
                ))}
              </nav>
              <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                <div className="input-group" style={{ width: '350px' }}>
                  <span className="input-group-text modern-search-icon">
                    <i className="bx bx-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control modern-search-input"
                    placeholder="Dokümanlarda ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                {documentAccess.create && <>
                  <button className="btn btn-modern-secondary" onClick={() => setShowFolderModal(true)}>
                    <i className="bx bx-folder-plus me-1" /> Yeni Klasör
                  </button>
                  <button className="btn btn-modern-primary" onClick={() => openUploadModal('single')} disabled={uploading}>
                    <i className="bx bx-upload me-1" /> Dosya Yükle
                  </button>
                  <button className="btn btn-modern-primary" onClick={() => openUploadModal('folder')} disabled={uploading}>
                    <i className="bx bx-folder-open me-1" /> Klasör Yükle
                  </button>
                </>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {uploading && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="modern-card p-4">
              <div className="d-flex align-items-center">
                <div className="modern-spinner spinner-border-sm me-3" role="status">
                  <span className="visually-hidden">Yükleniyor...</span>
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-medium">Dosya yükleniyor...</span>
                    <span className="text-muted">{uploadProgress}%</span>
                  </div>
                  <div className="progress" style={{ height: '6px' }}>
                    <div
                      className="progress-bar bg-primary"
                      role="progressbar"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-12">
          <div className="modern-card search-results-region" aria-busy={refreshing}>
            {refreshing && (
              <div className="search-refresh-indicator" role="status">
                <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                Sonuçlar güncelleniyor…
              </div>
            )}
            <div className="p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0 modern-table">
                  <thead>
                    <tr>
                      <th className="modern-table-header">Dosya Adı</th>
                      <th className="modern-table-header">Tür</th>
                      <th className="modern-table-header">Kategori</th>
                      <th className="modern-table-header">Boyut</th>
                      <th className="modern-table-header">Değiştirilme</th>
                      <th className="modern-table-header text-center">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDocuments.map((item) => (
                      <tr key={item._id || item.id} className="modern-table-row">
                        <td className="modern-table-cell">
                          <div className="d-flex align-items-center">
                            <div className="modern-file-icon me-3">
                              <i className={`${item.icon} ${item.color}`}></i>
                            </div>
                            <div>
                              <button
                                type="button"
                                className={`documents-item-name ${item.type === 'folder' ? 'is-folder' : ''}`}
                                onClick={() => item.type === 'folder' ? openFolder(item) : handleDownload(item._id || item.id)}
                              >
                                {item.name}
                              </button>
                              {item.description && (
                                <div className="text-muted small mt-1" style={{ fontSize: '0.75rem' }}>
                                  {item.description}
                                </div>
                              )}
                              <small className="text-muted">{getFileTypeLabel(item.type)}</small>
                            </div>
                          </div>
                        </td>
                        <td className="modern-table-cell">
                          <span className="modern-badge modern-badge-secondary">
                            {getFileTypeLabel(item.type)}
                          </span>
                        </td>
                        <td className="modern-table-cell">
                          <span className="modern-badge modern-badge-secondary">
                            {documentCategories.find((category) => category.id === item.resolvedCategory)?.shortLabel || 'Tümü'}
                          </span>
                        </td>
                        <td className="modern-table-cell text-muted">{formatFileSize(item.size)}</td>
                        <td className="modern-table-cell text-muted">
                          {new Date(item.modified).toLocaleDateString('tr-TR')}
                        </td>
                        <td className="modern-table-cell text-center">
                          <div className="modern-action-buttons">
                            {documentAccess.edit && <button
                              className="btn btn-sm modern-btn-icon"
                              title="Düzenle"
                              onClick={() => setEditingDocument(item)}
                            >
                              <i className="bx bx-edit"></i>
                            </button>}
                            <button
                              className="btn btn-sm modern-btn-icon"
                              title={item.type === 'folder' ? 'Klasörü aç' : 'İndir'}
                              onClick={() => handleDownload(item._id || item.id)}
                            >
                              <i className={`bx ${item.type === 'folder' ? 'bx-folder-open' : 'bx-download'}`}></i>
                            </button>
                            {documentAccess.delete && <button
                              className="btn btn-sm modern-btn-icon text-danger"
                              title="Sil"
                              onClick={() => handleDelete(item._id || item.id, item.name)}
                              style={{ color: '#dc3545' }}
                            >
                              <i className="bx bx-trash"></i>
                            </button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {pagination.pages > 1 && (
                <nav className="documents-pagination" aria-label="Doküman sayfaları">
                  <p className="documents-pagination-summary" aria-live="polite">
                    {((currentPage - 1) * pagination.limit) + 1}–{Math.min(currentPage * pagination.limit, pagination.total)} / {pagination.total} doküman
                  </p>
                  <div className="documents-pagination-controls">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                      disabled={currentPage === 1}
                      aria-label="Önceki sayfa"
                    >
                      <i className="bx bx-chevron-left" aria-hidden="true" />
                      <span>Önceki</span>
                    </button>
                    <div className="documents-pagination-pages">
                      {visiblePageNumbers.map((pageItem) => typeof pageItem === 'number' ? (
                        <button
                          type="button"
                          key={pageItem}
                          className={pageItem === currentPage ? 'active' : ''}
                          onClick={() => setCurrentPage(pageItem)}
                          aria-label={`${pageItem}. sayfa`}
                          aria-current={pageItem === currentPage ? 'page' : undefined}
                        >
                          {pageItem}
                        </button>
                      ) : (
                        <span key={pageItem} aria-hidden="true">…</span>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((page) => Math.min(page + 1, pagination.pages))}
                      disabled={currentPage === pagination.pages}
                      aria-label="Sonraki sayfa"
                    >
                      <span>Sonraki</span>
                      <i className="bx bx-chevron-right" aria-hidden="true" />
                    </button>
                  </div>
                </nav>
              )}
            </div>
          </div>
        </div>
      </div>

      {showUploadModal && (
        <div className="modern-modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modern-upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modern-upload-header">
              <div className="d-flex align-items-center">
                <div className="modern-upload-icon">
                  <i className="bx bx-cloud-upload"></i>
                </div>
                <div>
                  <h4 className="modern-upload-title mb-1">
                    {uploadMode === 'folder' ? 'Klasör Yükle' : 'Dosya Yükle'}
                  </h4>
                  <p className="modern-upload-subtitle">
                    {uploadMode === 'folder'
                      ? 'Seçtiğiniz klasör ve alt klasör yapısı korunur.'
                      : 'Bir veya birden fazla dosyayı sisteme ekleyin.'}
                  </p>
                </div>
              </div>
              <button className="modern-upload-close" onClick={() => setShowUploadModal(false)}>
                <i className="bx bx-x"></i>
              </button>
            </div>

            <div className="modern-upload-body">
              <div className="modern-drop-zone">
                <div className="modern-drop-content">
                  <div className="modern-drop-icon">
                    <i className="bx bx-cloud-upload"></i>
                  </div>
                  <h5 className="modern-drop-title">
                    {uploadMode === 'folder' ? 'Yüklenecek klasörü seçin' : 'Yüklenecek dosyaları seçin'}
                  </h5>
                  <p className="modern-drop-text">Alt klasörler ve birden fazla dosya desteklenir</p>
                  <input
                    type="file"
                    className="modern-file-input"
                    onChange={handleFileUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.mp4"
                    id="fileInput"
                    multiple
                    {...(uploadMode === 'folder' ? { webkitdirectory: '', directory: '' } : {})}
                  />
                  <label htmlFor="fileInput" className="modern-file-label">
                    {uploadMode === 'folder' ? 'Klasör Seç' : 'Dosya Seç'}
                  </label>
                </div>
                <div className="modern-drop-info">
                  <small className="text-muted">
                    <i className="bx bx-info-circle me-1"></i>
                    PDF, Office, metin, CSV, JPG, PNG veya MP4 (Maks: 1 GB)
                  </small>
                </div>
              </div>

              {uploadForm.files.length > 0 && (
                <div className="modern-file-preview">
                  <div className="modern-file-card">
                    <div className="modern-file-icon-preview">
                      <i className={`bx ${uploadMode === 'folder' ? 'bx-folder' : getFileIcon(uploadForm.files[0].name)}`}></i>
                    </div>
                    <div className="modern-file-details">
                      <h6 className="modern-file-name">
                        {uploadForm.files.length === 1 ? uploadForm.files[0].name : `${uploadForm.files.length} dosya seçildi`}
                      </h6>
                      <p className="modern-file-size">
                        {(uploadForm.files.reduce((sum, file) => sum + file.size, 0) / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      className="modern-file-remove"
                      onClick={() => setUploadForm((prev) => ({ ...prev, files: [], title: '' }))}
                    >
                      <i className="bx bx-x"></i>
                    </button>
                  </div>
                </div>
              )}

              <div className="modern-upload-form">
                <div className="modern-form-group">
                  <label className="modern-form-label" htmlFor="documentCategory">
                    <i className="bx bx-folder me-2"></i>
                    Kategori *
                  </label>
                  <select
                    id="documentCategory"
                    className="modern-form-input"
                    value={uploadForm.category}
                    onChange={(e) => setUploadForm((prev) => ({ ...prev, category: e.target.value }))}
                    disabled={Boolean(currentFolder)}
                  >
                    {documentCategories.filter((category) => category.id !== 'all').map((category) => (
                      <option key={category.id} value={category.id}>{category.title}</option>
                    ))}
                  </select>
                </div>

                {uploadMode === 'single' && uploadForm.files.length <= 1 && <div className="modern-form-group">
                  <label className="modern-form-label">
                    <i className="bx bx-edit me-2"></i>
                    Dosya Başlığı *
                  </label>
                  <input
                    type="text"
                    className="modern-form-input"
                    placeholder="Dosya başlığını girin..."
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm((prev) => ({ ...prev, title: e.target.value }))}
                  />
                </div>}

                <div className="modern-form-group">
                  <label className="modern-form-label">
                    <i className="bx bx-message-square-detail me-2"></i>
                    Açıklama
                  </label>
                  <textarea
                    className="modern-form-textarea"
                    rows="3"
                    placeholder="Dosya hakkında açıklama..."
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm((prev) => ({ ...prev, description: e.target.value }))}
                  ></textarea>
                </div>
              </div>
            </div>

            <div className="modern-upload-footer">
              <button className="modern-btn-cancel" onClick={() => setShowUploadModal(false)}>
                <i className="bx bx-x me-1"></i>
                İptal
              </button>
              <button
                className="modern-btn-upload"
                onClick={handleUploadSubmit}
                disabled={
                  uploading ||
                  uploadForm.files.length === 0 ||
                  (uploadMode === 'single' && uploadForm.files.length === 1 && !uploadForm.title.trim())
                }
              >
                {uploading ? (
                  <>
                    <span className="modern-spinner me-2"></span>
                    Yükleniyor...
                  </>
                ) : (
                  <>
                    <i className="bx bx-upload me-1"></i>
                    {uploadForm.files.length > 1 ? `${uploadForm.files.length} Dosyayı Yükle` : 'Yükle'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFolderModal && (
        <div className="modern-modal-overlay" onMouseDown={(event) => event.target === event.currentTarget && setShowFolderModal(false)}>
          <form className="document-edit-dialog" onSubmit={handleCreateFolder}>
            <header>
              <div><span>Doküman merkezi</span><h3>Yeni klasör</h3></div>
              <button type="button" onClick={() => setShowFolderModal(false)} aria-label="Kapat"><i className="bx bx-x" /></button>
            </header>
            <label>
              <span>Klasör adı</span>
              <input
                value={newFolderName}
                onChange={(event) => setNewFolderName(event.target.value)}
                placeholder="Örn. Çevre Prosedürleri"
                autoFocus
                required
              />
            </label>
            <p className="text-muted small mb-0">
              {currentFolder ? `${currentFolder.name} klasörünün altında oluşturulacak.` : 'Seçili doküman kategorisinin kökünde oluşturulacak.'}
            </p>
            <footer>
              <button type="button" className="role-secondary-button" onClick={() => setShowFolderModal(false)}>Vazgeç</button>
              <button className="role-primary-button" type="submit">Klasörü Oluştur</button>
            </footer>
          </form>
        </div>
      )}

      {editingDocument && <div className="modern-modal-overlay" onMouseDown={(event) => event.target === event.currentTarget && setEditingDocument(null)}><form className="document-edit-dialog" onSubmit={handleEditSubmit}><header><div><span>Doküman yönetimi</span><h3>Dokümanı düzenle</h3></div><button type="button" onClick={() => setEditingDocument(null)} aria-label="Kapat"><i className="bx bx-x" /></button></header><label><span>Başlık</span><input value={editingDocument.name || ''} onChange={(event) => setEditingDocument({ ...editingDocument, name: event.target.value })} required /></label><label><span>Kategori</span><select value={editingDocument.category || editingDocument.resolvedCategory || 'other'} onChange={(event) => setEditingDocument({ ...editingDocument, category: event.target.value })}>{documentCategories.filter((category) => category.id !== 'all').map((category) => <option key={category.id} value={category.id}>{category.title}</option>)}</select></label><label><span>Açıklama</span><textarea rows="4" value={editingDocument.description || ''} onChange={(event) => setEditingDocument({ ...editingDocument, description: event.target.value })} /></label><footer><button type="button" className="role-secondary-button" onClick={() => setEditingDocument(null)}>Vazgeç</button><button className="role-primary-button" type="submit">Kaydet</button></footer></form></div>}

      {filteredDocuments.length === 0 && (
        <div className="row">
          <div className="col-12">
            <div className="modern-empty-state text-center py-5">
              <i className="bx bx-folder-open display-1 text-muted mb-4" style={{ color: '#e9ecef' }}></i>
              <h4 className="fw-bold text-dark mb-3">
                {searchTerm ? 'Arama sonucu bulunamadı' : 'Bu klasör boş'}
              </h4>
              <p className="text-muted mb-4">
                {searchTerm
                  ? 'Aradığınız kriterlere uygun doküman bulunamadı.'
                  : 'Henüz bu klasörde hiç dosya bulunmuyor.'}
              </p>
              {documentAccess.create && <button className="btn btn-modern-primary px-4" onClick={() => openUploadModal('single')}>
                <i className="bx bx-upload me-1"></i>
                {searchTerm ? 'Yeni Dosya Yükle' : 'İlk Dosyayı Yükleyin'}
              </button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsPage;
